// MACHINERY V2 (Stroje) — every route into an SR surface, in ONE place (M2.6).
//
// >>> THIS FILE EXISTS BECAUSE THE SHARED EDIT TARGET IS EASY TO FORGET. <<<
// The create page and the edit page are the SAME component; which one you get is
// decided ONLY by `customerServiceReports.serviceReport` — an app-wide Redux
// slot (data package §8: "edit distinguished ONLY by redux `serviceReport`
// pre-populated"). So the caller, not the page, decides the mode, and a caller
// that forgets to clear the slot opens CREATE as an EDIT of whatever was
// selected last.
//
// CARRIED FIX (found at M2.5, deferred to M2.6 because M2.6 repoints these
// targets anyway). v1 clears it — `handleCreateServiceReport` in
// components/ServiceReports.tsx:167-170 is
//     const handleCreateServiceReport = () => {
//       dispatch(selectServiceReport(null));
//       navigate(`/machinery/${machineId}/service-reports/new`);
//     };
// (personally read in this worktree — ticket process rule (a)). Both v2 callers
// had only the navigate half: profile/useProfileActions.ts and
// fleet/useFleetHandlers.ts. From the v2 hero, "Vytvoriť servisnú správu" after
// opening any report therefore landed on a form pre-filled as an EDIT of that
// report — and its submit would PUT over it. Routing every entry point through
// `openSrCreate` makes the clear structural instead of remembered.
//
// The SR detail page makes this sharper, not looser: `getServiceReport.fulfilled`
// assigns the same slot (customerMachineServiceReportsSlice.ts:139-142), so
// merely VIEWING a report arms the trap.
//
// §9 D5 — THE SOLVE WIZARD IS NOT RE-SKINNED THIS CYCLE. `srSolvePath` points at
// the LIVE `/machinery/:id/service-reports/:n/solve` page on purpose. It is the
// one SR route that must NOT be repointed at v2.
//
// M2.6 REVIEW R4 — AND IT CARRIES A RETURN TOKEN. That live page hard-codes all
// four of its exits to `/machinery/:id`, so solving a report opened from v2 ended
// on the V1 profile. `?returnTo=v2` is a FIXED TOKEN — not a path, not a URL: the
// wizard tests it for EXACT equality with the literal "v2" and then builds
// `/machinery-v2/:id` from its OWN route param, so nothing this file sends is
// ever navigated to. Absent or different value = that page's original exits,
// unchanged. See the R4 block at components/SolveServiceReportPage.tsx:148-167.
import { NavigateFunction } from "react-router-dom";

import { selectServiceReport } from "../../customerMachineServiceReportsSlice";
import { ServiceReport } from "../../models/ServiceReportsModel";

/** Anything that can dispatch — typed structurally so this module does not have
 *  to import the store type (which would make it a circular dependency). */
type Dispatch = (action: any) => unknown;

export const MACHINERY_V2_BASE = "/machinery-v2";

export const srCreatePath = (machineId: string): string =>
  `${MACHINERY_V2_BASE}/${machineId}/service-reports/new`;

export const srDetailPath = (machineId: string, reportNumber: string): string =>
  `${MACHINERY_V2_BASE}/${machineId}/service-reports/${reportNumber}`;

/** LIVE page, deliberately (§9 D5) — plus the R4 return token, so its Save and
 *  its Cancel come back to the V2 profile the user actually started from. */
export const srSolvePath = (machineId: string, reportNumber: string): string =>
  `/machinery/${machineId}/service-reports/${reportNumber}/solve?returnTo=v2`;

/** Blank create form. ALWAYS clears the shared edit target first. */
export const openSrCreate = (
  dispatch: Dispatch,
  navigate: NavigateFunction,
  machineId: string
): void => {
  dispatch(selectServiceReport(null));
  navigate(srCreatePath(machineId));
};

/** Edit form for `report`. The report — not a URL param — IS the mode switch,
 *  and `report.number` is what the PUT keys off (data package §8). */
export const openSrEdit = (
  dispatch: Dispatch,
  navigate: NavigateFunction,
  machineId: string,
  report: ServiceReport
): void => {
  dispatch(selectServiceReport(report));
  navigate(srCreatePath(machineId));
};
